import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import type { DriverApi, ParcelRecord, ScanOutcome } from "../driver-api.ts";
import { nextSequence } from "../device.ts";
import { SCAN_SCOPE, type ScanLocation } from "../offline-queue.ts";
import {
  NO_SHIPMENT_SUB,
  NO_SHIPMENT_TITLE,
  OUT_FOR_DELIVERY,
  PARCEL_READ_SCOPE,
  PICKED_UP,
  POSITIONLESS_SUB,
  POSITIONLESS_TITLE,
  scanChip,
  scanChipIsDesigned,
  scanSubLine,
  scanTitle,
  START_DELIVERY_BLOCKED,
} from "../scan.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { HeroScanFrame } from "./Hero.tsx";
import { PhoneShell } from "./PhoneShell.tsx";
import { StateScreen } from "./StateScreen.tsx";

/**
 * `5 Scan` — the parcel is with the courier, and the platform has said so.
 *
 * The design: the `scan` hero — a 190×128 card with a green border and a barcode across it — the
 * `['done','scan confirmed']` chip, _"Parcel par_3N0E is with you"_,
 * _"Scan 1146 · next: Oyster Bay, 3.1 km"_, and the slide-to-confirm `Start delivery`.
 *
 * `src/scan.ts` carries the reasoning for every value, and in particular for the one the mobile
 * sweep marks this screen degraded on: the next stop, which no read can reach from a parcel
 * identifier and which is therefore drawn as a sentence rather than as a place.
 *
 * ## The scan happens, and that is the screen
 *
 * This is the only one of the three new screens whose first act is a **command**: the device records
 * the scan, LOG-06 answers `recorded`, `duplicate` or `stale`, and the chip is that answer. The
 * parcel read runs beside it and answers the headline. Both are told their identifiers by the
 * address, which is where a barcode reader plugs in — Q-127.
 *
 * `StrictMode` double-invokes effects in development, which for a command matters: the guard is the
 * scan's own identity rather than a flag, because `scanId` and `sourceSequence` are allocated once
 * per mount and LOG-06 answers `duplicate` to the second send of the same pair. So the double
 * invocation is **correct** rather than suppressed — it exercises the deduplication the contract
 * publishes — and the ref below only stops a third number being spent.
 */

export interface ScanProps {
  readonly api: DriverApi;
  readonly tenantId: string;
  /** The shipment the label names, from the address. Absent is a state, not an error. */
  readonly shipmentId: string | undefined;
  /** The parcel the label names, from the address. Absent costs the headline and nothing else. */
  readonly parcelId: string | undefined;
  readonly now: () => number;
  /** This device's position, for the scan's required `location`. Injected so it can be driven. */
  readonly position: () => Promise<GeolocationPosition>;
}

interface Scanned {
  readonly outcome: ScanOutcome | undefined;
  readonly sequence: number;
  readonly parcel: ParcelRecord | undefined;
  /** True when this device gave no position, so LOG-06's required `location` had no value. */
  readonly positionless: boolean;
}

export function Scan(props: ScanProps): ReactNode {
  const { api, tenantId, shipmentId, parcelId } = props;
  const [scanned, setScanned] = useState<Scanned | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });
  const [sending, setSending] = useState(false);
  const [started, setStarted] = useState(false);
  /** The one scan this mount records, allocated once so a re-render cannot spend a second number. */
  const pickup = useRef<{ scanId: string; sequence: number } | undefined>(undefined);

  const record = useCallback(() => {
    if (shipmentId === undefined) return;
    setState({ kind: "loading" });
    pickup.current ??= { scanId: globalThis.crypto.randomUUID(), sequence: nextSequence() };
    const scan = pickup.current;

    Promise.all([
      locationFor(props.position),
      parcelId === undefined
        ? Promise.resolve(undefined)
        : api.parcel({ tenantId, parcelId }).catch(() => undefined),
    ])
      .then(async ([location, parcel]) => {
        if (location === undefined) {
          setScanned({ outcome: undefined, sequence: scan.sequence, parcel, positionless: true });
          setState({ kind: "ready" });
          return;
        }
        const outcome = await api.ingestScan({
          scanId: scan.scanId,
          shipmentId,
          ...(parcelId === undefined ? {} : { parcelId }),
          sourceSequence: scan.sequence,
          type: PICKED_UP,
          location,
          occurredAt: new Date(props.now()).toISOString(),
        });
        setScanned({ outcome, sequence: scan.sequence, parcel, positionless: false });
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => {
        setState(stateForError(error, [SCAN_SCOPE, PARCEL_READ_SCOPE]));
      });
  }, [api, tenantId, shipmentId, parcelId]);

  useEffect(record, [record]);

  /**
   * `Start delivery` — a second scan, of LOG-06's own `out_for_delivery` type.
   *
   * A command and not a retry: a fresh `scanId` and the next number in this device's sequence, so a
   * courier who slides twice sends two scans and LOG-06 answers `duplicate` to the second on its own
   * terms. The one thing it must not do is reuse the pickup scan's number, which would be a scan
   * claiming to be a scan that already happened.
   */
  const start = useCallback(() => {
    if (shipmentId === undefined || sending) return;
    setSending(true);
    void (async (): Promise<void> => {
      const location = await locationFor(props.position);
      /*
       * No position, no scan, and the screen moves to the state that says so.
       *
       * LOG-06's `location` is required and publishes two shapes, so there is nothing to send — and
       * sending the scan without it would be inventing a third. A courier who turned location off
       * between the two slides lands on the same screen the first slide would have put them on.
       */
      if (location === undefined) {
        setScanned((held) => (held === undefined ? held : { ...held, positionless: true }));
        setSending(false);
        return;
      }
      await api.ingestScan({
        scanId: globalThis.crypto.randomUUID(),
        shipmentId,
        ...(parcelId === undefined ? {} : { parcelId }),
        sourceSequence: nextSequence(),
        type: OUT_FOR_DELIVERY,
        location,
        occurredAt: new Date(props.now()).toISOString(),
      });
      /*
       * `true` whatever the outcome word was, and that is deliberate.
       *
       * `recorded`, `duplicate` and a 201 with no body all mean the delivery has started; `stale`
       * cannot happen here, because this scan carries the highest number this device has issued. So
       * what is kept is the fact rather than the word — the word belongs to the pickup scan the chip
       * is otherwise about.
       */
      setStarted(true);
      setSending(false);
    })().catch((error: unknown) => {
      setSending(false);
      setState(stateForError(error, [SCAN_SCOPE]));
    });
  }, [api, shipmentId, parcelId, sending]);

  if (shipmentId === undefined) {
    return (
      <StateScreen
        screen="scan"
        chip={{ class: "unknown", label: "nothing scanned" }}
        title={NO_SHIPMENT_TITLE}
        sub={NO_SHIPMENT_SUB}
      />
    );
  }

  if (state.kind !== "ready" || scanned === undefined) {
    return (
      <StateScreen
        screen="scan"
        state={state}
        onRetry={record}
        purpose="Sending the scan and looking up the parcel."
      />
    );
  }

  if (scanned.positionless) {
    /*
     * The one state this screen cannot draw as the design draws it, because the scan never left.
     *
     * LOG-06's `location` is required and publishes two shapes, neither of which a courier with no
     * position can fill. Said in the design's vocabulary with this build's words — Q-129 asks
     * whether a position-less scan should exist at all.
     */
    return (
      <StateScreen
        screen="scan"
        chip={{ class: "human", label: "action_required" }}
        title={POSITIONLESS_TITLE}
        sub={POSITIONLESS_SUB}
      />
    );
  }

  const headline = scanTitle(scanned.parcel);
  const sub = scanSubLine(scanned.sequence);
  /*
   * A scan that was not recorded does not get the next one.
   *
   * `stale` is the case this is for: the parcel moved on without this scan, so sending
   * `out_for_delivery` behind it would put a number into a sequence whose predecessor the service
   * has already superseded. `duplicate` is fine — the design says so — and `recorded` is the design's
   * own state.
   */
  const blocked = scanned.outcome === "stale" ? START_DELIVERY_BLOCKED : undefined;

  return (
    <PhoneShell
      screen="scan"
      /* `art:'scan'` — the design's bordered card with its barcode. `screens/scan.css` has the geometry. */
      art={<HeroScanFrame />}
      /* `on:1`, the design's own flag: a courier holding a parcel is on shift. */
      online
      earningsLabel={null}
      chip={started ? { class: "active", label: "on the way" } : scanChip(scanned.outcome)}
      title={headline.title}
      {...(headline.absence === undefined ? {} : { titleAbsence: headline.absence })}
      sub={sub.text}
      actions={[
        {
          label: "Start delivery",
          glyph: "navigation",
          primary: true,
          /*
           * `slide`, the design's fourth element. Its notes: _"slide-to-confirm for accept, arrive,
           * start and complete"_ — starting is the third of those four.
           */
          slide: true,
          ...(blocked === undefined ? { onPress: start } : { unavailable: blocked }),
        },
      ]}
      /*
       * Always flagged, and that is the honest reading rather than an oversight.
       *
       * The design's sub-line has two halves and this build can only draw one of them, so the words
       * on this screen are never entirely the design's — whatever LOG-06 answered. The chip's own
       * agreement with the design is asserted separately by the tests, which is where the
       * distinction between "the chip is right" and "the line is complete" belongs.
       */
      flag={
        scanChipIsDesigned(scanned.outcome) && !started
          ? "the next stop has no source"
          : "wording not in the design"
      }
    />
  );
}

/**
 * The scan's required `location`, or nothing, in which case the scan is not sent.
 *
 * **LOG-06 publishes two shapes for this field and no third.** `{kind:'hub',hubId}` and
 * `{kind:'geo',latitude,longitude}` — and a courier at a merchant's door is not at a hub and has no
 * `hubId` to send. So a device that refuses geolocation leaves this application with nothing it may
 * put in a required field, and the screen says so rather than sending a coordinate of `0,0`, which
 * is a real place in the Gulf of Guinea and is the single worst value this application could put on
 * a custody record. Inventing a third `kind` is the other thing it must not do.
 *
 * That is a genuine dead end rather than a treatment: a scan with no position cannot be recorded at
 * all. Q-129 asks whether LOG-06 should publish a position-less scan, since a parcel scanned in a
 * basement is the ordinary case rather than the exception.
 */
async function locationFor(
  position: () => Promise<GeolocationPosition>,
): Promise<ScanLocation | undefined> {
  try {
    const here = await position();
    return { kind: "geo", latitude: here.coords.latitude, longitude: here.coords.longitude };
  } catch {
    return undefined;
  }
}
